import {
  registerControl,
} from "./controlCore.js";

const BASE_BUTTON_CLASSES = [
  "flex min-h-10 w-full items-center justify-between gap-2 rounded-md sm:min-h-[30px]",
  "border border-gray-200 bg-white px-2 py-1 text-left text-base text-gray-700 sm:text-sm",
  "transition-colors hover:border-[var(--dfc-brand-primary)] focus:outline-none",
].join(" ");
const MENU_CLASSES = [
  "absolute left-0 right-0 z-30 overflow-auto",
  "rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl",
].join(" ");
const PORTAL_MENU_CLASSES = [
  "fixed z-50 overflow-auto",
  "rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl",
].join(" ");
const FOCUS_SHADOW = [
  "0 0 0 1.5px rgba(71, 85, 105, 0.55)",
].join(", ");
const FOCUS_BORDER_COLOR = "#e5e7eb";
const INVALID_SHADOW = "0 0 0 1.5px rgba(220, 38, 38, 0.22)";
const DEFAULT_SEARCH_THRESHOLD = 7;
const BRAND_VARIABLE_NAMES = [
  "--dfc-brand-primary",
  "--dfc-brand-primary-contrast",
  "--dfc-brand-primary-hover",
  "--dfc-brand-accent",
  "--dfc-brand-secondary-accent",
  "--dfc-brand-primary-ring",
  "--dfc-brand-surface-tint",
];
const OPTION_CLASSES = [
  "flex w-full items-center gap-2 rounded-lg px-2.5 py-3 text-left text-base sm:py-2 sm:text-sm",
  "text-gray-700 transition-colors hover:bg-gray-50",
].join(" ");

let openSelectState = null;
let scrollLockedState = null;
let scrollLockSnapshot = null;
let nextSelectInstanceId = 1;

function createSelectInstanceId() {
  const id = `select-control-${nextSelectInstanceId}`;
  nextSelectInstanceId += 1;
  return id;
}

function normalizeHexColor(color = "") {
  const value = String(color || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(value)) {return value;}
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
  }
  return "";
}

function getContrastTextColor(color = "") {
  const hex = normalizeHexColor(color);
  if (!hex) {return "#111827";}

  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.62 ? "#111827" : "#ffffff";
}

function getSelectedOptions(state) {
  return state.selectedValues
    .map((value) => findOption(state.options, value))
    .filter(Boolean);
}

function toValueArray(value) {
  if (Array.isArray(value)) {return value.map((entry) => String(entry));}
  if (value === null || value === undefined || value === "") {return [];}
  return [String(value)];
}

function getSelectedValue(value, multiple) {
  const values = toValueArray(value);
  return multiple ? values : values[0] || "";
}

function optionMatchesQuery(option, query) {
  if (!query) {return true;}
  return option.searchText.includes(query.toLowerCase());
}

function getVisibleOptions(options, query) {
  return options.filter((option) => !option.hidden && optionMatchesQuery(option, query));
}

function isOptionAddable(option) {
  return !option.disabled && !option.archived && !option.inactive && !option.selectedOnly;
}

function isSelectedOption(state, option) {
  return state.selectedValues.includes(String(option.value));
}

function shouldShowOptionInMenu(state, option) {
  const isSelected = state?.multiple && isSelectedOption(state, option);
  if (state?.checkboxMenu) {
    return isSelected || isOptionAddable(option);
  }
  return !isSelected && isOptionAddable(option);
}

function getMenuOptions(options, query, state = null) {
  return getVisibleOptions(options, query).filter((option) => {
    return shouldShowOptionInMenu(state, option);
  });
}

function getEnabledOptions(options, state) {
  return options.filter((option) => {
    const isSelected = isSelectedOption(state, option);
    return !option.disabled && (isOptionAddable(option) || state.multiple && isSelected);
  });
}

function findOption(options, value) {
  return options.find((option) => String(option.value) === String(value));
}

function getBulkSelectableOptions(state) {
  return state.options.filter((option) => {
    return !option.hidden && isOptionAddable(option);
  });
}

function isAllBulkSelected(state) {
  const selectableOptions = getBulkSelectableOptions(state);
  return selectableOptions.length > 0 && selectableOptions.every((option) => {
    return state.selectedValues.includes(String(option.value));
  });
}

function getOptionId(state, option) {
  const optionIndex = state.options.findIndex((entry) => {
    return String(entry.value) === String(option.value);
  });
  return `${state.optionIdPrefix}-${Math.max(0, optionIndex)}`;
}

function getActiveOptionId(state) {
  const activeOption = state.activeValue ? findOption(state.options, state.activeValue) : null;
  if (!activeOption) {return "";}
  return getOptionId(state, activeOption);
}

function setElementText(element, text) {
  element.textContent = text || "";
}

function bindInvertHover(button, foregroundColor, backgroundColor) {
  const reset = () => {
    button.style.backgroundColor = "";
    button.style.color = foregroundColor;
  };
  const invert = () => {
    button.style.backgroundColor = foregroundColor;
    button.style.color = backgroundColor;
  };

  reset();
  button.addEventListener("mouseenter", invert);
  button.addEventListener("mouseleave", reset);
  button.addEventListener("focus", invert);
  button.addEventListener("blur", reset);
}

function createMaterialIcon(name, sizeClass = "text-[18px]") {
  const icon = document.createElement("span");
  icon.className = `material-symbols-outlined pointer-events-none ${sizeClass} leading-none`;
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = name;
  return icon;
}

function getBrandAccentColor(state) {
  return getComputedStyle(state.root).getPropertyValue("--dfc-brand-accent").trim() || "#f78183";
}

function createAccentDot(option) {
  const dot = document.createElement("span");
  dot.className = "h-2.5 w-2.5 shrink-0 rounded-full border border-black/5";
  dot.setAttribute("aria-hidden", "true");
  dot.style.backgroundColor = option.accentColor || "#d1d5db";
  return dot;
}

function createCheckboxMarker(checked, state = null) {
  const marker = document.createElement("span");
  marker.className = [
    "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
    checked ? "" : "border-gray-300 bg-white text-transparent",
  ].join(" ");
  marker.setAttribute("aria-hidden", "true");
  if (checked) {
    const accentColor = getBrandAccentColor(state);
    marker.style.backgroundColor = accentColor;
    marker.style.borderColor = accentColor;
    marker.style.color = "#ffffff";
  }

  marker.append(createMaterialIcon("check", "text-[18px] sm:text-[13px]"));
  return marker;
}

function createStateLabel(option) {
  if (!option.inactive && !option.archived && !option.selectedOnly) {return null;}

  const label = document.createElement("span");
  label.className = "ml-auto shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500";
  label.textContent = option.unavailableLabel || (option.inactive ? "inactive" : "archived");
  return label;
}

function renderSelectionLabel(state) {
  const selectedOptions = getSelectedOptions(state);

  if (state.checkboxMenu) {
    if (selectedOptions.length === 0) {return state.emptySelectionLabel;}
    if (isAllBulkSelected(state)) {return state.allSelectedLabel;}
    return `${state.selectedCountLabel} (${selectedOptions.length})`;
  }

  if (state.multiple) {
    if (selectedOptions.length === 0) {return state.placeholder;}
    return selectedOptions.map((option) => getSelectedOptionLabel(option)).join(", ");
  }

  return selectedOptions[0] ? getSelectedOptionLabel(selectedOptions[0]) : state.placeholder;
}

function getSelectedOptionLabel(option) {
  if (option.inactive) {return `${option.label} (inactive)`;}
  if (option.unavailableLabel) {return `${option.label} (${option.unavailableLabel})`;}
  if (option.archived || option.selectedOnly) {return `${option.label} (archived)`;}
  return option.label;
}

function removeSelectedValue(state, value, event) {
  event?.preventDefault();
  event?.stopPropagation();
  if (state.disabled || state.readOnly) {return;}

  const nextValues = state.selectedValues.filter((selectedValue) => {
    return String(selectedValue) !== String(value);
  });
  state.handle.setValue(nextValues);
}

function renderSelectedPills(container, state) {
  container.replaceChildren();

  if (!state.multiple || state.selectedValues.length === 0) {return;}

  getSelectedOptions(state)
    .forEach((option) => {
      const pill = document.createElement("span");
      pill.className = [
        "inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5",
        "!text-sm font-medium !leading-5 shadow-sm sm:!text-xs sm:!leading-normal",
      ].join(" ");
      pill.setAttribute("role", "listitem");
      if (option.accentColor) {
        pill.style.backgroundColor = option.accentColor;
        pill.style.color = getContrastTextColor(option.accentColor);
      } else {
        pill.classList.add("bg-gray-200", "text-gray-700");
      }

      const label = document.createElement("span");
      label.className = "min-w-0 truncate";
      label.textContent = getSelectedOptionLabel(option);
      pill.append(label);

      if (!state.disabled && !state.readOnly) {
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = [
          "-mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full sm:h-3.5 sm:w-3.5",
          "text-current opacity-75 transition-opacity hover:opacity-100 focus:opacity-100",
          "focus:outline-none",
        ].join(" ");
        removeButton.setAttribute("aria-label", `Remove ${getSelectedOptionLabel(option)}`);
        bindInvertHover(
          removeButton,
          option.accentColor ? getContrastTextColor(option.accentColor) : "#9ca3af",
          option.accentColor || "#ffffff",
        );
        removeButton.append(createMaterialIcon("close", "text-[14px] sm:text-[13px]"));
        removeButton.addEventListener("click", (event) => {
          removeSelectedValue(state, option.value, event);
        });
        removeButton.addEventListener("keydown", (event) => {
          event.stopPropagation();
        });
        pill.append(removeButton);
      }

      container.append(pill);
    });
}

function groupOptions(options) {
  const groups = new Map();

  options.forEach((option) => {
    const group = option.group || "";
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group).push(option);
  });

  return Array.from(groups.entries());
}

function updateActiveOption(state, delta) {
  const enabledOptions = getEnabledOptions(getMenuOptions(state.options, state.query, state), state);
  if (enabledOptions.length === 0) {
    state.activeValue = "";
    return;
  }

  const currentIndex = enabledOptions.findIndex((option) => option.value === state.activeValue);
  const nextIndex = currentIndex === -1 ?
    0 :
    (currentIndex + delta + enabledOptions.length) % enabledOptions.length;
  state.activeValue = enabledOptions[nextIndex].value;
}

function setFirstActiveOption(state) {
  const enabledOptions = getEnabledOptions(getMenuOptions(state.options, state.query, state), state);
  state.activeValue = enabledOptions[0]?.value || "";
}

function focusActiveControl(state) {
  if (state.button) {
    state.button.focus({preventScroll: true});
    return;
  }
  requestAnimationFrame(() => {
    state.button?.focus({preventScroll: true});
  });
}

function isControlInvalid(state) {
  return Boolean(state?.root?.hasAttribute("data-control-invalid"));
}

function applyInvalidFieldStyle(element, state) {
  if (!isControlInvalid(state)) {return;}
  element.style.borderColor = "#dc2626";
  element.style.boxShadow = INVALID_SHADOW;
}

function getBrandPrimaryColor(state) {
  const brandOwner = state?.root?.closest?.("#sandbox-view") || state?.root;
  return getComputedStyle(brandOwner || document.documentElement)
    .getPropertyValue("--dfc-brand-primary")
    .trim() || "#ffd612";
}

function copyBrandVariablesToPortal(state, element) {
  if (!state?.portalMenu || !element) {return;}
  const rootStyles = getComputedStyle(state.root);
  BRAND_VARIABLE_NAMES.forEach((name) => {
    const value = rootStyles.getPropertyValue(name).trim();
    if (value) {
      element.style.setProperty(name, value);
    }
  });
}

function getRestingBorderColor(state) {
  if (isControlInvalid(state)) {return "#dc2626";}
  if (!state?.multiple) {
    const selectedOption = getSelectedOptions(state)[0] || null;
    return selectedOption?.accentColor || "";
  }
  return "";
}

function bindHoverBorder(element, state = null) {
  const apply = () => {
    if (document.activeElement === element || isControlInvalid(state) || element.disabled) {return;}
    element.style.borderColor = getBrandPrimaryColor(state);
  };
  const reset = () => {
    if (document.activeElement === element) {return;}
    element.style.borderColor = getRestingBorderColor(state);
  };

  element.addEventListener("pointerenter", apply);
  element.addEventListener("mouseover", apply);
  element.addEventListener("mouseenter", apply);
  element.addEventListener("pointerleave", reset);
  element.addEventListener("mouseleave", reset);
}

function bindFocusShadow(element, state = null) {
  const apply = () => {
    element.style.borderColor = isControlInvalid(state) ? "#dc2626" : FOCUS_BORDER_COLOR;
    element.style.boxShadow = isControlInvalid(state) ? INVALID_SHADOW : FOCUS_SHADOW;
  };
  const reset = () => {
    element.style.borderColor = getRestingBorderColor(state);
    element.style.boxShadow = isControlInvalid(state) ? INVALID_SHADOW : "";
  };
  element.addEventListener("focus", apply);
  element.addEventListener("blur", reset);
}

function getOpenMenu(state) {
  return document.getElementById(state.listboxId);
}

function isWheelEventOverMenu(event, menu) {
  if (!menu) {return false;}
  if (menu.contains(event.target)) {return true;}
  if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {return false;}
  const pointTarget = document.elementFromPoint(event.clientX, event.clientY);
  return Boolean(pointTarget && menu.contains(pointTarget));
}

function handleLockedWheel(event) {
  if (!scrollLockedState?.open) {return;}

  const menu = getOpenMenu(scrollLockedState);
  if (isWheelEventOverMenu(event, menu)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
}

function handleLockedTouchMove(event) {
  if (!scrollLockedState?.open) {return;}
  const menu = getOpenMenu(scrollLockedState);
  if (menu?.contains(event.target)) {
    return;
  }
  event.preventDefault();
}

function lockPageScroll(state) {
  if (scrollLockedState === state) {return;}
  if (scrollLockedState) {
    unlockPageScroll(scrollLockedState);
  }
  scrollLockedState = state;
  scrollLockSnapshot = {
    alreadyFixed: document.body.style.position === "fixed",
    scrollY: window.scrollY || document.documentElement.scrollTop || 0,
    position: document.body.style.position,
    top: document.body.style.top,
    width: document.body.style.width,
  };
  if (!scrollLockSnapshot.alreadyFixed) {
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollLockSnapshot.scrollY}px`;
    document.body.style.width = "100%";
  }
  document.addEventListener("wheel", handleLockedWheel, {passive: false, capture: true});
  document.addEventListener("touchmove", handleLockedTouchMove, {passive: false, capture: true});
}

function unlockPageScroll(state) {
  if (!scrollLockedState || scrollLockedState !== state) {return;}
  document.removeEventListener("wheel", handleLockedWheel, {capture: true});
  document.removeEventListener("touchmove", handleLockedTouchMove, {capture: true});
  const scrollY = scrollLockSnapshot?.scrollY || 0;
  if (scrollLockSnapshot?.alreadyFixed !== true) {
    document.body.style.position = scrollLockSnapshot?.position || "";
    document.body.style.top = scrollLockSnapshot?.top || "";
    document.body.style.width = scrollLockSnapshot?.width || "";
    window.scrollTo(0, scrollY);
  }
  scrollLockedState = null;
  scrollLockSnapshot = null;
}

function selectOption(state, option) {
  if (!option || option.disabled) {return;}

  if (state.multiple) {
    const value = String(option.value);
    const selected = new Set(state.selectedValues);
    const shouldRefocusSearch = state.searchFocused;
    if (selected.has(value)) {
      selected.delete(value);
    } else {
      if (!isOptionAddable(option)) {return;}
      selected.add(value);
    }
    state.handle.setValue(Array.from(selected));
    if (state.checkboxMenu) {
      if (shouldRefocusSearch) {
        requestAnimationFrame(() => state.searchInput?.focus({preventScroll: true}));
      } else {
        focusActiveControl(state);
      }
      return;
    }
    closeMenu(state, {returnFocus: true});
    return;
  }

  if (!isOptionAddable(option)) {return;}
  state.handle.setValue(String(option.value));
  closeMenu(state, {returnFocus: true});
}

function clearSelection(state, event) {
  event?.preventDefault();
  event?.stopPropagation();
  if (state.disabled || state.readOnly) {return;}
  state.handle.setValue(state.multiple ? [] : "");
}

function toggleBulkSelection(state, event) {
  event?.preventDefault();
  event?.stopPropagation();
  if (state.disabled || state.readOnly || !state.checkboxMenu) {return;}

  if (state.selectedValues.length === 0) {
    state.handle.setValue(getBulkSelectableOptions(state).map((option) => String(option.value)));
    focusActiveControl(state);
    return;
  }

  state.handle.setValue([]);
  focusActiveControl(state);
}

function openMenu(state) {
  if (state.disabled || state.readOnly) {return;}
  if (openSelectState && openSelectState !== state) {
    closeMenu(openSelectState, {returnFocus: false});
  }
  state.open = true;
  openSelectState = state;
  setFirstActiveOption(state);
  renderSelect(state);
  lockPageScroll(state);
  focusActiveControl(state);
}

function closeMenu(state, options = {}) {
  const wasOpen = state.open;
  state.open = false;
  state.query = "";
  state.activeValue = "";
  if (openSelectState === state) {
    openSelectState = null;
  }
  unlockPageScroll(state);
  renderSelect(state);
  if (wasOpen && options.returnFocus) {
    state.button?.focus();
  }
}

function toggleMenu(state) {
  if (state.open) {
    closeMenu(state);
  } else {
    openMenu(state);
  }
}

function handleKeydown(state, event) {
  if (state.disabled || state.readOnly) {return;}
  const isSearchTarget = event.target === state.searchInput;

  if (event.key === "Home" && state.open) {
    event.preventDefault();
    setFirstActiveOption(state);
    updateRenderedActiveOption(state, {scroll: true});
    if (isSearchTarget) {
      state.searchInput?.focus({preventScroll: true});
    } else {
      focusActiveControl(state);
    }
  }

  if (event.key === "End" && state.open) {
    event.preventDefault();
    const enabledOptions = getEnabledOptions(getMenuOptions(state.options, state.query, state), state);
    state.activeValue = enabledOptions.at(-1)?.value || "";
    updateRenderedActiveOption(state, {scroll: true});
    if (isSearchTarget) {
      state.searchInput?.focus({preventScroll: true});
    } else {
      focusActiveControl(state);
    }
  }

  if (event.key === "ArrowDown") {
    event.preventDefault();
    if (!state.open) {
      openMenu(state);
      return;
    }
    updateActiveOption(state, 1);
    updateRenderedActiveOption(state, {scroll: true});
    if (isSearchTarget) {
      state.searchInput?.focus({preventScroll: true});
    } else {
      focusActiveControl(state);
    }
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();
    if (!state.open) {
      openMenu(state);
      return;
    }
    updateActiveOption(state, -1);
    updateRenderedActiveOption(state, {scroll: true});
    if (isSearchTarget) {
      state.searchInput?.focus({preventScroll: true});
    } else {
      focusActiveControl(state);
    }
  }

  if (event.key === "Enter" || event.key === " ") {
    if (isSearchTarget && event.key === " ") {return;}
    if (!state.open) {
      event.preventDefault();
      openMenu(state);
      return;
    }

    const option = findOption(state.options, state.activeValue);
    if (option) {
      event.preventDefault();
      selectOption(state, option);
    }
  }

  if (event.key === "Escape") {
    event.preventDefault();
    closeMenu(state, {returnFocus: true});
  }
}

function bindDocumentClose(state) {
  state.documentClickHandler = (event) => {
    const menu = getOpenMenu(state);
    if (!state.root.contains(event.target) && !menu?.contains(event.target)) {
      closeMenu(state, {returnFocus: false});
    }
  };
  document.addEventListener("pointerdown", state.documentClickHandler);
}

function unbindDocumentClose(state) {
  if (state.documentClickHandler) {
    document.removeEventListener("pointerdown", state.documentClickHandler);
    state.documentClickHandler = null;
  }
  if (state.scrollHandler) {
    window.removeEventListener("scroll", state.scrollHandler, true);
    state.scrollHandler = null;
  }
  unlockPageScroll(state);
}

function renderMultiSelectTrigger(state) {
  const shell = document.createElement("div");
  shell.id = state.buttonId;
  shell.className = BASE_BUTTON_CLASSES;
  if (!state.checkboxMenu && state.selectedValues.length > 0) {
    shell.classList.add("pl-[3px]", "pr-2", "sm:py-[3px]");
  }
  if (state.disabled) {
    shell.classList.add("cursor-not-allowed", "border-gray-200", "bg-gray-100", "text-gray-400");
    shell.classList.remove("bg-white", "text-gray-700", "hover:border-[var(--dfc-brand-primary)]");
  }
  applyInvalidFieldStyle(shell, state);
  shell.tabIndex = state.disabled ? -1 : 0;
  shell.setAttribute("role", "combobox");
  shell.setAttribute("aria-haspopup", "listbox");
  shell.setAttribute("aria-expanded", String(state.open));
  shell.setAttribute("aria-disabled", String(state.disabled));
  shell.setAttribute("aria-multiselectable", "true");
  shell.setAttribute("aria-label", `${state.accessibleName}: ${renderSelectionLabel(state)}`);
  shell.setAttribute("aria-controls", state.listboxId);
  shell.setAttribute("aria-describedby", state.validationMessageId);
  shell.setAttribute("aria-invalid", String(isControlInvalid(state)));
  const activeOptionId = getActiveOptionId(state);
  if (activeOptionId) {
    shell.setAttribute("aria-activedescendant", activeOptionId);
  }

  const content = document.createElement("span");
  content.className = "flex min-w-0 flex-1 flex-wrap items-center gap-1.5";
  content.setAttribute("role", state.selectedValues.length > 0 ? "list" : "presentation");
  if (state.checkboxMenu && state.clearable && state.selectedValues.length > 0 &&
      !state.disabled && !state.readOnly) {
    content.classList.add("pr-6");
  }

  if (state.checkboxMenu) {
    content.setAttribute("role", "presentation");
    const label = document.createElement("span");
    label.className = "min-w-0 truncate";
    if (state.selectedValues.length === 0) {
      label.classList.add("text-gray-400");
    }
    const hasPartialSelection = state.selectedValues.length > 0 && !isAllBulkSelected(state);
    if (hasPartialSelection) {
      label.classList.add("font-semibold");
      label.style.color = getBrandAccentColor(state);
    }
    setElementText(label, renderSelectionLabel(state));
    content.append(label);
    shell.append(content);

    const arrow = createMaterialIcon(state.open ? "expand_less" : "expand_more");
    arrow.classList.add("shrink-0");
    shell.append(arrow);
    shell.addEventListener("click", (event) => {
      if (event.target.closest("button")) {return;}
      toggleMenu(state);
    });
    shell.addEventListener("keydown", (event) => handleKeydown(state, event));
    bindHoverBorder(shell, state);
    bindFocusShadow(shell, state);
    state.button = shell;
    return shell;
  }

  const pills = document.createElement("span");
  pills.className = "flex min-w-0 max-w-full flex-wrap gap-1";
  renderSelectedPills(pills, state);

  if (state.selectedValues.length > 0) {
    content.append(pills);
  } else {
    const label = document.createElement("span");
    label.className = "min-w-0 truncate text-gray-400";
    setElementText(label, state.placeholder);
    content.append(label);
  }

  shell.append(content);

  const arrow = createMaterialIcon(state.open ? "expand_less" : "expand_more");
  arrow.classList.add("shrink-0");
  shell.append(arrow);
  shell.addEventListener("click", (event) => {
    if (event.target.closest("button")) {return;}
    toggleMenu(state);
  });
  shell.addEventListener("keydown", (event) => handleKeydown(state, event));
  bindHoverBorder(shell, state);
  bindFocusShadow(shell, state);
  state.button = shell;
  return shell;
}

function renderButton(state) {
  const button = document.createElement("button");
  button.type = "button";
  button.id = state.buttonId;
  button.className = BASE_BUTTON_CLASSES;
  button.disabled = state.disabled;
  if (state.disabled) {
    button.classList.add("cursor-not-allowed", "border-gray-200", "bg-gray-100", "text-gray-400");
    button.classList.remove("bg-white", "text-gray-700", "hover:border-[var(--dfc-brand-primary)]");
  }
  applyInvalidFieldStyle(button, state);
  button.setAttribute("aria-haspopup", "listbox");
  button.setAttribute("aria-expanded", String(state.open));
  button.setAttribute("aria-controls", state.listboxId);
  button.setAttribute("aria-label", `${state.accessibleName}: ${renderSelectionLabel(state)}`);
  button.setAttribute("aria-describedby", state.validationMessageId);
  button.setAttribute("aria-invalid", String(isControlInvalid(state)));
  const activeOptionId = getActiveOptionId(state);
  if (activeOptionId) {
    button.setAttribute("aria-activedescendant", activeOptionId);
  }

  const content = document.createElement("span");
  content.className = "flex min-w-0 flex-1 flex-wrap items-center gap-1.5";
  if (!state.multiple && state.clearable && state.selectedValues.length > 0 &&
      !state.disabled && !state.readOnly) {
    content.classList.add("pr-6");
  }

  const pills = document.createElement("span");
  pills.className = "flex min-w-0 max-w-full flex-wrap gap-1";
  renderSelectedPills(pills, state);

  if (state.multiple && state.selectedValues.length > 0) {
    content.append(pills);
  } else {
    const selectedOption = getSelectedOptions(state)[0] || null;
    if (!state.disabled && !state.multiple && selectedOption?.accentColor) {
      button.style.backgroundColor = selectedOption.accentColor;
      button.style.borderColor = selectedOption.accentColor;
      button.style.color = getContrastTextColor(selectedOption.accentColor);
    }

    const label = document.createElement("span");
    label.className = state.selectedValues.length > 0 ?
      "min-w-0 truncate" :
      "min-w-0 truncate text-gray-400";
    setElementText(label, renderSelectionLabel(state));
    content.append(label);
  }

  button.append(content);

  const arrow = createMaterialIcon(state.open ? "expand_less" : "expand_more");
  arrow.classList.add("shrink-0");
  button.append(arrow);
  button.addEventListener("click", () => toggleMenu(state));
  button.addEventListener("keydown", (event) => handleKeydown(state, event));
  bindHoverBorder(button, state);
  bindFocusShadow(button, state);
  state.button = button;
  return button;
}

function renderClearButton(state) {
  if (state.multiple && !state.checkboxMenu ||
      !state.clearable ||
      state.selectedValues.length === 0 ||
      state.disabled || state.readOnly) {
    return null;
  }

  const clearButton = document.createElement("button");
  clearButton.type = "button";
  clearButton.className = [
    "absolute right-8 top-1/2 inline-flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center rounded-full",
    "transition-opacity focus:outline-none",
  ].join(" ");
  if (state.open) {
    clearButton.tabIndex = -1;
  }
  clearButton.setAttribute("aria-label", "Clear selection");
  clearButton.setAttribute("aria-controls", state.buttonId);
  const selectedOption = getSelectedOptions(state)[0] || null;
  bindInvertHover(
    clearButton,
    selectedOption?.accentColor ? getContrastTextColor(selectedOption.accentColor) : "#9ca3af",
    selectedOption?.accentColor || "#ffffff",
  );
  clearButton.append(createMaterialIcon("close", "text-[13px]"));
  clearButton.addEventListener("click", (event) => clearSelection(state, event));
  return clearButton;
}

function shouldRenderSearch(state) {
  if (!state.searchable) {return false;}
  const searchableOptions = state.options.filter((option) => {
    return !option.hidden && isOptionAddable(option);
  });
  return searchableOptions.length >= state.searchThreshold;
}

function shouldRenderBulkSelection(state) {
  return Boolean(state.bulkSelection && shouldRenderSearch(state));
}

function createSearchInput(state) {
  if (!shouldRenderSearch(state)) {
    state.searchInput = null;
    return null;
  }

  const input = document.createElement("input");
  input.id = state.searchInputId;
  input.type = "search";
  input.value = state.query;
  input.className = [
    "menu-search-input mb-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm",
    "hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  ].join(" ");
  input.placeholder = state.searchPlaceholder;
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", String(state.open));
  input.setAttribute("aria-controls", state.listboxId);
  input.setAttribute("aria-label", `${state.accessibleName} search`);
  input.setAttribute("aria-describedby", state.validationMessageId);
  const activeOptionId = getActiveOptionId(state);
  if (activeOptionId) {
    input.setAttribute("aria-activedescendant", activeOptionId);
  }
  input.addEventListener("focus", () => {
    state.searchFocused = true;
    input.style.borderColor = FOCUS_BORDER_COLOR;
  });
  input.addEventListener("blur", () => {
    state.searchFocused = false;
    input.style.borderColor = "";
  });
  input.addEventListener("input", () => {
    const shouldRefocusSearch = state.searchFocused;
    state.query = input.value;
    setFirstActiveOption(state);
    renderSelect(state);
    if (shouldRefocusSearch) {
      requestAnimationFrame(() => state.searchInput?.focus({preventScroll: true}));
    }
  });
  input.addEventListener("keydown", (event) => handleKeydown(state, event));
  state.searchInput = input;
  return input;
}

function createSearchClearButton(state) {
  const button = document.createElement("button");
  const hasQuery = Boolean(state.query);
  button.type = "button";
  button.className = [
    "absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full",
    "text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)]",
    "focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]",
    hasQuery ? "inline-flex" : "hidden",
  ].join(" ");
  button.disabled = !hasQuery;
  button.setAttribute("aria-hidden", String(!hasQuery));
  button.setAttribute("aria-label", "Clear search");
  button.append(createMaterialIcon("close", "text-[18px] leading-none"));
  button.addEventListener("click", () => {
    state.query = "";
    renderSelect(state);
    state.searchInput?.focus({preventScroll: true});
  });
  button.addEventListener("keydown", (event) => event.stopPropagation());
  return button;
}

function createSearchControl(state, options = {}) {
  const input = createSearchInput(state);
  if (!input) {return null;}

  input.classList.remove("mb-1", "px-2");
  input.classList.add("pl-2", "pr-8");

  const frame = document.createElement("span");
  frame.className = options.block ?
    "relative mb-1 block w-full" :
    "relative min-w-0 flex-1";
  frame.append(input, createSearchClearButton(state));
  return frame;
}

function renderSearch(state, menu) {
  const searchControl = createSearchControl(state, {block: true});
  if (!searchControl) {return;}
  menu.append(searchControl);
}

function renderCheckboxToolbar(state, menu) {
  if (!state.checkboxMenu) {return;}

  const toolbar = document.createElement("div");
  toolbar.className = "mb-1 flex items-center gap-1.5";

  const searchControl = createSearchControl(state);
  if (searchControl) {
    toolbar.append(searchControl);
  }

  if (shouldRenderBulkSelection(state)) {
    const button = document.createElement("button");
    const selectedCount = state.selectedValues.length;
    const bulkTitle = selectedCount === 0 ? "Select all" : "Clear selection";
    button.type = "button";
    button.className = [
      "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200",
      "text-gray-500 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]",
      "focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
    ].join(" ");
    button.title = bulkTitle;
    button.setAttribute("aria-label", bulkTitle);
    button.append(createMaterialIcon(selectedCount === 0 ? "done_all" : "remove_done", "text-[18px]"));
    button.addEventListener("click", (event) => toggleBulkSelection(state, event));
    button.addEventListener("keydown", (event) => event.stopPropagation());
    toolbar.append(button);
  }

  if (toolbar.childElementCount > 0) {
    menu.append(toolbar);
  }
}

function renderEmptyState(state, menu, message) {
  const empty = document.createElement("div");
  empty.id = state.emptyStateId;
  empty.className = "px-3 py-4 text-center text-sm text-gray-500";
  empty.setAttribute("role", "status");
  empty.textContent = message;
  menu.append(empty);
}

function scrollActiveOptionIntoView(state) {
  const menu = getOpenMenu(state);
  const activeOptionId = getActiveOptionId(state);
  const activeOption = activeOptionId ? document.getElementById(activeOptionId) : null;
  if (!menu || !activeOption || !menu.contains(activeOption)) {return;}

  const menuRect = menu.getBoundingClientRect();
  const optionRect = activeOption.getBoundingClientRect();
  if (optionRect.top < menuRect.top) {
    menu.scrollTop -= menuRect.top - optionRect.top;
    return;
  }
  if (optionRect.bottom > menuRect.bottom) {
    menu.scrollTop += optionRect.bottom - menuRect.bottom;
  }
}

function getMenuBoundaryRect(state) {
  const configuredBoundary = typeof state.menuBoundary === "string" ?
    state.root.closest?.(state.menuBoundary) ||
      document.querySelector(state.menuBoundary) :
    state.menuBoundary;
  if (configuredBoundary?.getBoundingClientRect) {
    const boundaryRect = configuredBoundary.getBoundingClientRect();
    return {
      top: Math.max(0, boundaryRect.top),
      bottom: Math.min(
        window.innerHeight || document.documentElement.clientHeight,
        boundaryRect.bottom,
      ),
    };
  }

  if (state.portalMenu) {
    return {
      top: 0,
      bottom: window.innerHeight || document.documentElement.clientHeight,
    };
  }

  const boundary = state.root.closest?.("#form-fields");
  if (boundary) {
    return boundary.getBoundingClientRect();
  }

  return {
    top: 0,
    bottom: window.innerHeight || document.documentElement.clientHeight,
  };
}

function updateRenderedActiveOption(state, options = {}) {
  const menu = getOpenMenu(state);
  if (!menu) {return;}
  const activeOptionId = getActiveOptionId(state);
  if (activeOptionId) {
    state.button?.setAttribute("aria-activedescendant", activeOptionId);
    state.searchInput?.setAttribute("aria-activedescendant", activeOptionId);
  }
  menu.querySelectorAll('[role="option"]').forEach((optionElement) => {
    const isActive = String(optionElement.dataset.value) === String(state.activeValue);
    optionElement.style.backgroundColor = isActive ? "#f9fafb" : "";
    optionElement.classList.toggle("text-gray-900", isActive);
  });
  if (options.scroll) {
    scrollActiveOptionIntoView(state);
  }
}

function renderOption(state, option) {
  const isSelected = isSelectedOption(state, option);
  const isActive = String(option.value) === String(state.activeValue);
  const isUnavailable = option.disabled ||
    (!isOptionAddable(option) && !(state.multiple && isSelected));
  const button = document.createElement("button");
  button.type = "button";
  button.id = getOptionId(state, option);
  button.className = OPTION_CLASSES;
  button.disabled = isUnavailable;
  button.setAttribute("role", "option");
  button.setAttribute("aria-selected", String(isSelected));
  button.tabIndex = -1;
  button.dataset.value = option.value;
  button.addEventListener("wheel", (event) => {
    const menu = getOpenMenu(state);
    if (!menu) {return;}
    menu.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  }, {passive: false});

  if (isActive) {
    button.style.backgroundColor = "#f9fafb";
    button.classList.add("text-gray-900");
  }
  if (isSelected && !state.checkboxMenu) {
    button.style.color = getBrandAccentColor(state);
  }

  if (isUnavailable) {
    button.classList.add("cursor-not-allowed", "opacity-60");
  }

  if (state.checkboxMenu) {
    button.append(createCheckboxMarker(isSelected, state));
  } else if (option.accentColor) {
    button.append(createAccentDot(option));
  }

  const label = document.createElement("span");
  label.className = "min-w-0 flex-1 truncate";
  label.textContent = option.label;
  if (isSelected && !state.checkboxMenu) {
    label.classList.add("font-medium");
  }
  button.append(label);

  const stateLabel = createStateLabel(option);
  if (stateLabel) {
    button.append(stateLabel);
  }

  if (isSelected && !state.checkboxMenu) {
    const check = createMaterialIcon("check", "text-[18px]");
    check.classList.add("ml-auto", "shrink-0");
    check.style.color = getBrandAccentColor(state);
    button.append(check);
  }

  button.addEventListener("click", () => selectOption(state, option));
  button.addEventListener("mouseenter", () => {
    if (String(state.activeValue) === String(option.value)) {return;}
    state.activeValue = option.value;
    updateRenderedActiveOption(state);
  });
  return button;
}

function renderOptions(state, menu) {
  const menuOptions = getMenuOptions(state.options, state.query, state);
  const hasMenuCandidates = state.options.some((option) => {
    return !option.hidden && shouldShowOptionInMenu(state, option);
  });
  if (!hasMenuCandidates) {
    renderEmptyState(state, menu, state.emptyMessage);
    return;
  }
  if (menuOptions.length === 0) {
    renderEmptyState(state, menu, state.noResultsMessage);
    return;
  }

  groupOptions(menuOptions).forEach(([group, options]) => {
    if (group) {
      const heading = document.createElement("div");
      heading.className = "px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400";
      heading.setAttribute("role", "presentation");
      heading.textContent = group;
      menu.append(heading);
    }

    options.forEach((option) => {
      menu.append(renderOption(state, option));
    });
  });
}

function positionMenu(state, menu) {
  const boundaryRect = getMenuBoundaryRect(state);
  const rootRect = state.root.getBoundingClientRect();
  const gap = 6;
  const viewportPadding = 8;
  const preferredHeight = 288;
  const spaceBelow = boundaryRect.bottom - rootRect.bottom - viewportPadding;
  const spaceAbove = rootRect.top - boundaryRect.top - viewportPadding;
  const placeAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
  const availableSpace = Math.max(96, (placeAbove ? spaceAbove : spaceBelow) - gap);

  menu.style.maxHeight = `${Math.min(preferredHeight, availableSpace)}px`;
  if (state.portalMenu) {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const width = Math.min(
      Math.max(0, rootRect.width),
      Math.max(0, viewportWidth - viewportPadding * 2),
    );
    const left = Math.min(
      Math.max(viewportPadding, rootRect.left),
      Math.max(viewportPadding, viewportWidth - width - viewportPadding),
    );
    menu.style.left = `${left}px`;
    menu.style.right = "auto";
    menu.style.width = `${width}px`;
    menu.style.top = placeAbove ? "auto" : `${rootRect.bottom + gap}px`;
    menu.style.bottom = placeAbove ?
      `${Math.max(viewportPadding, window.innerHeight - rootRect.top + gap)}px` :
      "auto";
    return;
  }

  if (placeAbove) {
    menu.style.top = "auto";
    menu.style.bottom = `calc(100% + ${gap}px)`;
  } else {
    menu.style.top = `calc(100% + ${gap}px)`;
    menu.style.bottom = "auto";
  }
}

function renderMenu(state) {
  if (!state.open) {return null;}

  const menu = document.createElement("div");
  menu.id = state.listboxId;
  menu.className = state.portalMenu ? PORTAL_MENU_CLASSES : MENU_CLASSES;
  copyBrandVariablesToPortal(state, menu);
  menu.setAttribute("role", "listbox");
  menu.setAttribute("aria-labelledby", state.buttonId);
  menu.setAttribute("aria-multiselectable", String(state.multiple));
  menu.addEventListener("wheel", (event) => {
    menu.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  }, {passive: false, capture: true});
  if (state.checkboxMenu) {
    renderCheckboxToolbar(state, menu);
  } else {
    renderSearch(state, menu);
  }
  renderOptions(state, menu);
  requestAnimationFrame(() => positionMenu(state, menu));
  return menu;
}

function renderSelect(state) {
  document.getElementById(state.listboxId)?.remove();
  state.root.replaceChildren();
  state.root.classList.add("relative");
  state.root.toggleAttribute("data-select-open", state.open);
  state.root.toggleAttribute("data-select-disabled", state.disabled);

  state.root.append(state.multiple ? renderMultiSelectTrigger(state) : renderButton(state));
  const clearButton = renderClearButton(state);
  if (clearButton) {
    state.root.append(clearButton);
  }
  const menu = renderMenu(state);
  if (menu) {
    if (state.portalMenu) {
      document.body.append(menu);
    } else {
      state.root.append(menu);
    }
  }
  if (state.validationMessageElement) {
    state.root.append(state.validationMessageElement);
  }
}

function normalizeSelectOptions(options = []) {
  return (Array.isArray(options) ? options : []).map((option) => ({
    value: String(option.value ?? ""),
    label: String(option.label ?? option.value ?? ""),
    disabled: Boolean(option.disabled),
    archived: Boolean(option.archived || option.selectedOnly),
    inactive: Boolean(option.inactive),
    hidden: Boolean(option.hidden),
    group: option.group || "",
    accentColor: option.accentColor || "",
    order: Number(option.order ?? 9999),
    searchText: String(option.searchText || `${option.label ?? ""} ${option.value ?? ""}`).toLowerCase(),
    source: option.source || option,
    selectedOnly: Boolean(option.selectedOnly),
    unavailableLabel: option.unavailableLabel || "",
  }));
}

/**
 * Mounts a custom select control for future sandbox and migration use.
 * @param {object} config Select control configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {string} [config.name] Field name used for registered value extraction.
 * @param {Array<object>} [config.options] Standard control options.
 * @param {string|Array<string>} [config.value] Initial selected value or values.
 * @param {boolean} [config.multiple=false] Enable multi-select array values.
 * @param {boolean} [config.searchable=false] Enable visible-label search.
 * @param {number} [config.searchThreshold=7] Minimum option count before search appears.
 * @param {boolean} [config.checkboxMenu=false] Render multi-select options as checkbox-style rows.
 * @param {boolean} [config.bulkSelection=false] Show select-all/clear action for checkbox menus.
 * @param {string} [config.emptySelectionLabel] Label shown for empty checkbox menus.
 * @param {string} [config.allSelectedLabel] Label shown for all-selected checkbox menus.
 * @param {string} [config.selectedCountLabel] Label prefix for partial checkbox-menu selection.
 * @param {boolean} [config.clearable=false] Show clear action when selected.
 * @param {boolean} [config.disabled=false] Disable interaction and extraction.
 * @param {boolean} [config.readOnly=false] Render selection without opening menu.
 * @param {boolean} [config.required=false] Require at least one selected value.
 * @param {boolean} [config.portalMenu=false] Render the menu at document body
 *   level for controls mounted inside clipped overflow containers.
 * @param {HTMLElement|string} [config.menuBoundary] Element or closest-query
 *   selector used as the vertical flip boundary.
 * @param {Function} [config.onChange] Change callback receiving canonical value.
 * @returns {object} Select lifecycle handle.
 */
export function createSelectControl(config) {
  const id = config.id || createSelectInstanceId();
  const state = {
    id,
    root: config.root,
    name: config.name || "",
    options: normalizeSelectOptions(config.options),
    selectedValues: toValueArray(config.value),
    multiple: Boolean(config.multiple),
    searchable: Boolean(config.searchable),
    searchThreshold: Number.isFinite(config.searchThreshold) ?
      Math.max(0, Number(config.searchThreshold)) :
      DEFAULT_SEARCH_THRESHOLD,
    checkboxMenu: Boolean(config.checkboxMenu && config.multiple),
    bulkSelection: Boolean(config.bulkSelection),
    emptySelectionLabel: config.emptySelectionLabel || "None",
    allSelectedLabel: config.allSelectedLabel || config.placeholder || "+ Add item",
    selectedCountLabel: config.selectedCountLabel || "Selected items",
    clearable: Boolean(config.clearable),
    disabled: Boolean(config.disabled),
    readOnly: Boolean(config.readOnly),
    required: Boolean(config.required),
    portalMenu: Boolean(config.portalMenu || config.portal),
    menuBoundary: config.menuBoundary || null,
    placeholder: config.placeholder || "+ Add item",
    searchPlaceholder: config.searchPlaceholder || "Search options",
    emptyMessage: config.emptyMessage || "No options available.",
    noResultsMessage: config.noResultsMessage || "No matching options.",
    accessibleName: config.accessibleName || config.label || config.name || "Select value",
    query: "",
    open: false,
    activeValue: "",
    button: null,
    searchInput: null,
    searchFocused: false,
    documentClickHandler: null,
    scrollHandler: null,
    validationObserver: null,
    validationMessageElement: document.createElement("p"),
    buttonId: `${id}-button`,
    searchInputId: `${id}-search`,
    listboxId: `${id}-listbox`,
    optionIdPrefix: `${id}-option`,
    validationMessageId: `${id}-validation`,
    emptyStateId: `${id}-empty`,
    handle: null,
  };

  state.root.id = state.root.id || state.id;
  state.validationMessageElement.className = [
    "mt-1 hidden text-xs font-medium text-red-600",
  ].join(" ");
  state.validationMessageElement.id = state.validationMessageId;
  state.validationMessageElement.setAttribute("aria-live", "polite");

  const controlHandle = registerControl({
    root: state.root,
    name: state.name,
    value: getSelectedValue(state.selectedValues, state.multiple),
    multiple: state.multiple,
    required: state.required,
    requiredMessage: config.requiredMessage || "Required",
    disabled: state.disabled,
    validationMessageElement: state.validationMessageElement,
    mirror: config.mirror,
    mirrorInput: config.mirrorInput,
    getValue: () => getSelectedValue(state.selectedValues, state.multiple),
    setValue: (value) => {
      state.selectedValues = toValueArray(value);
      renderSelect(state);
      if (typeof config.onChange === "function") {
        config.onChange(getSelectedValue(state.selectedValues, state.multiple));
      }
    },
    mount: () => {
      bindDocumentClose(state);
      renderSelect(state);
      state.validationObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.attributeName === "data-control-invalid")) {
          renderSelect(state);
        }
      });
      state.validationObserver.observe(state.root, {attributes: true});
    },
    update: (updates) => {
      if ("options" in updates) {
        state.options = normalizeSelectOptions(updates.options);
      }
      if ("disabled" in updates) {state.disabled = Boolean(updates.disabled);}
      if ("readOnly" in updates) {state.readOnly = Boolean(updates.readOnly);}
      if ("placeholder" in updates) {state.placeholder = updates.placeholder;}
      renderSelect(state);
    },
    destroy: () => {
      state.validationObserver?.disconnect();
      state.validationObserver = null;
      unbindDocumentClose(state);
      document.getElementById(state.listboxId)?.remove();
      if (openSelectState === state) {
        openSelectState = null;
      }
      unlockPageScroll(state);
      state.root.replaceChildren();
    },
  });

  state.handle = controlHandle;
  controlHandle.open = () => {
    openMenu(state);
  };
  controlHandle.close = () => {
    closeMenu(state, {returnFocus: true});
  };

  return controlHandle;
}
